import { LocationType, type MapPoint } from '../types';
import { BoundingBoxes } from './bounding-boxes';
import { METRO_CITY_FOCUS_ZOOM } from './constants';
import type { NormalizedGeographySelections } from './geography-adapter';
import { isMetropolitanAreaLabel } from './location-type-utils';

export type CenterZoomBounds = {
  type: 'center-zoom';
  center: [number, number];
  zoom: number;
};

export type BoundsResult = mapboxgl.LngLatBoundsLike | CenterZoomBounds | null;

export function isCenterZoomBounds(v: BoundsResult): v is CenterZoomBounds {
  return (
    v !== null &&
    typeof v === 'object' &&
    'type' in v &&
    v.type === 'center-zoom'
  );
}

function extendBoundsWithLocation(
  locationType: LocationType,
  name: string,
  currentBounds: {
    minLon: number;
    minLat: number;
    maxLon: number;
    maxLat: number;
    hasBounds: boolean;
  }
): {
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
  hasBounds: boolean;
} {
  const bboxCollection = BoundingBoxes[locationType];
  if (!bboxCollection) {
    return currentBounds;
  }

  const bbox = bboxCollection[name];
  if (!bbox || bbox.length !== 4) {
    return currentBounds;
  }

  const [bboxMinLon, bboxMinLat, bboxMaxLon, bboxMaxLat] = bbox;

  if (
    typeof bboxMinLon === 'number' &&
    Number.isFinite(bboxMinLon) &&
    typeof bboxMinLat === 'number' &&
    Number.isFinite(bboxMinLat) &&
    typeof bboxMaxLon === 'number' &&
    Number.isFinite(bboxMaxLon) &&
    typeof bboxMaxLat === 'number' &&
    Number.isFinite(bboxMaxLat)
  ) {
    return {
      minLon: Math.min(currentBounds.minLon, bboxMinLon),
      minLat: Math.min(currentBounds.minLat, bboxMinLat),
      maxLon: Math.max(currentBounds.maxLon, bboxMaxLon),
      maxLat: Math.max(currentBounds.maxLat, bboxMaxLat),
      hasBounds: true,
    };
  }

  return currentBounds;
}

export interface BoundsCalculationInput {
  points?: MapPoint[];
  geographySelections?: NormalizedGeographySelections;
}

interface NormalizedLocationData {
  coordinates: MapPoint[];
  polygons: Record<LocationType, string[]>;
}

function normalizeLocationData(
  input: BoundsCalculationInput
): NormalizedLocationData | null {
  const { points = [], geographySelections } = input;

  if (geographySelections) {
    const { regions, countries, metros, cities, states } = geographySelections;

    return {
      coordinates: cities,
      polygons: {
        [LocationType.REGION]: regions,
        [LocationType.COUNTRY]: countries,
        [LocationType.STATE]: states,
        [LocationType.COUNTY]: [],
        [LocationType.METRO_AREA]: metros,
        [LocationType.CITY]: [],
      },
    };
  }

  if (points.length === 0) {
    return null;
  }

  const coordinatePoints = points.filter(
    (p) =>
      p.locationType === LocationType.METRO_AREA ||
      p.locationType === LocationType.CITY ||
      p.locationType === LocationType.STATE ||
      isMetropolitanAreaLabel(p.label)
  );

  const polygonPoints = points.filter(
    (p) =>
      p.locationType === LocationType.COUNTRY ||
      p.locationType === LocationType.REGION
  );

  const polygons: Record<LocationType, string[]> = {
    [LocationType.COUNTRY]: [],
    [LocationType.REGION]: [],
    [LocationType.STATE]: [],
    [LocationType.COUNTY]: [],
    [LocationType.METRO_AREA]: [],
    [LocationType.CITY]: [],
  };

  for (const p of polygonPoints) {
    let name = p.label?.trim() || '';
    if (name.endsWith('Non-Metro')) {
      name = name.replace(/Non-Metro$/, '').trim();
    }

    if (p.locationType === LocationType.REGION) {
      polygons[LocationType.REGION].push(name.toLowerCase());
    } else if (p.locationType === LocationType.COUNTRY) {
      if (
        name === 'Reunion' &&
        polygons[LocationType.COUNTRY].includes('france')
      ) {
        continue;
      }
      polygons[LocationType.COUNTRY].push(name.toLowerCase());
    }
  }

  return {
    coordinates: coordinatePoints,
    polygons,
  };
}

export function calculateBounds(input: BoundsCalculationInput): BoundsResult {
  const normalizedData = normalizeLocationData(input);
  if (!normalizedData) {
    return null;
  }

  const { coordinates, polygons } = normalizedData;

  if (coordinates.length > 0) {
    const validPoints = coordinates.filter(
      (p) => Number.isFinite(p.lon) && Number.isFinite(p.lat)
    );

    if (validPoints.length === 1) {
      const location = validPoints[0];
      if (location) {
        return {
          type: 'center-zoom',
          center: [location.lon, location.lat],
          zoom: METRO_CITY_FOCUS_ZOOM,
        };
      }
    } else if (validPoints.length > 1) {
      const lons = validPoints.map((p) => p.lon);
      const lats = validPoints.map((p) => p.lat);
      return [
        [Math.min(...lons), Math.min(...lats)],
        [Math.max(...lons), Math.max(...lats)],
      ];
    }
  }

  let bounds = {
    minLon: Infinity,
    minLat: Infinity,
    maxLon: -Infinity,
    maxLat: -Infinity,
    hasBounds: false,
  };

  Object.entries(polygons).forEach(([locationType, names]) => {
    if (!names?.length) return;

    names.forEach((name) => {
      bounds = extendBoundsWithLocation(
        locationType as LocationType,
        name,
        bounds
      );
    });
  });

  coordinates.forEach((location) => {
    const lon = location.lon;
    const lat = location.lat;
    if (Number.isFinite(lon) && Number.isFinite(lat)) {
      bounds.minLon = Math.min(bounds.minLon, lon);
      bounds.maxLon = Math.max(bounds.maxLon, lon);
      bounds.minLat = Math.min(bounds.minLat, lat);
      bounds.maxLat = Math.max(bounds.maxLat, lat);
      bounds.hasBounds = true;
    }
  });

  if (bounds.hasBounds) {
    return [
      [bounds.minLon, bounds.minLat],
      [bounds.maxLon, bounds.maxLat],
    ];
  }

  return null;
}
